import { type FC } from "react"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"

import type { RecordType, TableStatPlotView } from "types/dashboard.types"

import useTableStatConfig from "../configuration/useTableStatConfig"
import TableStatCell from "./TableStatCell"

interface TableStatProps {
  view: TableStatPlotView
  data: RecordType[]
}

const TableStat: FC<TableStatProps> = ({ view, data }) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const theme = useTheme()

  const {
    isEmpty,
    columns,
    rows,
    recordsByField,
    gridTemplateColumns,
    suffix,
    decimalPlaces,
    threshold,
    seasonalThreshold,
  } = useTableStatConfig({ view, data })

  if (isEmpty) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Typography variant="body2" color="text.secondary">
          {t("plots.NO_DATA", "No data")}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ height: "100%", pt: 2 }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowX: "auto", display: "flex" }}>
        <Box
          sx={{
            minWidth: 300,
            flex: 1,
            display: "grid",
            gridTemplateColumns,
            gridTemplateRows: `auto repeat(${rows.length}, 1fr)`,
            justifyContent: "space-between",
            columnGap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "subgrid",
              gridColumn: "1 / -1",
              columnGap: 1.5,
              alignItems: "center",
              pb: 1,
              borderBottom: `1px solid ${theme.palette.neutral[400]}`,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                variant="caption"
                color="text.secondary"
                textAlign={column.align ?? "left"}
                noWrap
              >
                {t(column.labelKey)}
              </Typography>
            ))}
          </Box>
          {rows.map((row, index) => (
            <Box
              key={row.field}
              sx={{
                display: "grid",
                gridTemplateColumns: "subgrid",
                gridColumn: "1 / -1",
                columnGap: 1.5,
                alignItems: "center",
                py: 1.5,
                borderTop:
                  index > 0 ? `1px solid ${theme.palette.neutral[300]}` : undefined,
              }}
            >
              {columns.map((column) => (
                <Box key={column.key} sx={{ minWidth: 0, width: "100%" }}>
                  <TableStatCell
                    column={column}
                    row={row}
                    records={recordsByField[row.field] ?? []}
                    suffix={suffix}
                    decimalPlaces={decimalPlaces}
                    threshold={threshold}
                    seasonalThreshold={seasonalThreshold}
                    locale={locale}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  )
}

export default TableStat
